import React, { useEffect, useState, Fragment } from 'react';
import ProfileSummary from './ProfileSummary';
import Functions from '../../api/Functions';
import M from 'materialize-css';
import { getRandomCode } from '../../utils/CodeUtil';
import { Link } from 'react-router-dom';
import JSONPretty from 'react-json-pretty';

const refModalDelete = React.createRef();

const ProfileList = ({ profiles, updateProfiles = () => {} }) => {
  const refModal = React.createRef();
  const [currentProfile, setCurrentProfile] = useState(null);
  const [inviteCode, setInviteCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [codeEdited, setCodeEdited] = useState(false);
  const [sent, setSent] = useState(false);
  const [errorMessageInModal, setErrorMessageInModal] = useState(null);
  const [deleteResults, setDeleteResults] = useState(null);
  const [emailConfirm, setEmailConfirm] = useState('');

  useEffect(() => {
    let elements = document.querySelectorAll('.modal');
    M.Modal.init(elements);
  }, []);

  const openModal = (profile) => {
    setCurrentProfile(profile);
    generateInviteCode();
    M.Modal.getInstance(refModal.current).open();
  };

  const closeModal = () => {
    const modal = document.getElementById('modal1');
    M.Modal.getInstance(modal).close();
  };

  const generateInviteCode = () => {
    setIsLoading(true);
    const code = getRandomCode();
    verifyInviteCodeUnique(code)
      .then((exists) => {
        if (exists) generateInviteCode();
        else setInviteCode(code);
        setIsLoading(false);
      })
      .catch((e) => {
        setIsLoading(false);
        setErrorMessageInModal(e.message);
      });
  };

  const verifyInviteCodeUnique = (inviteCode) => {
    return new Promise((resolve, reject) => {
      Functions.inviteCodeExists(inviteCode)
        .then(({ data }) => resolve(data))
        .catch((e) => reject(e));
    });
  };

  const editCode = (code) => {
    setInviteCode(code);
    setErrorMessageInModal(null);
    setCodeEdited(true);
    setCurrentProfile(null);
    setIsLoading(false);
  };

  const sendEmail = () => {
    const validationError = validateInviteCode();
    if (validationError) {
      setErrorMessageInModal(validationError);
      return;
    }
    sendInviteCodeToUserIfUnique();
  };

  const validateInviteCode = () => {
    if (!inviteCode) {
      return 'Invite code empty';
    }
    if (inviteCode.length < 6) {
      return 'Invite code should contain at least 6 characters.';
    }
    return false;
  };

  const sendInviteCodeToUserIfUnique = () => {
    setIsLoading(true);
    if (codeEdited) {
      checkIfEnteredCodeUnique();
    } else {
      sendInviteCode();
    }
  };

  const checkIfEnteredCodeUnique = () => {
    verifyInviteCodeUnique(inviteCode)
      .then((exists) => {
        if (exists) {
          setErrorMessageInModal('The code already exists');
          setIsLoading(false);
        } else sendInviteCode();
      })
      .catch((e) => {
        setIsLoading(false);
        setErrorMessageInModal(e.message);
      });
  };

  const sendInviteCode = async () => {
    setCodeEdited(true);
    setErrorMessageInModal(null);
    Functions.sendInviteCodeToUser(currentProfile.id, currentProfile.email, inviteCode)
      .then(({ data }) => {
        showSuccessOrErrorMessages(data);
        finishIfNoErrors(data);
      })
      .catch((error) => {
        setErrorMessageInModal(error && error.message ? error.message : error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const finishIfNoErrors = ({ sendEmailError, sendSmsError }) => {
    if (!sendEmailError && !sendSmsError) {
      closeModal();
      clearFields();
    }
    if (!sendEmailError || !sendSmsError) {
      setSent(true);
    }
  };

  const showSuccessOrErrorMessages = ({ sendEmailError, sendSmsError }) => {
    let errorMessage = getErrorMessage('Email', sendEmailError);
    errorMessage += getErrorMessage('SMS', sendSmsError);
    if (errorMessage) {
      setErrorMessageInModal(errorMessage);
    }
  };

  const getErrorMessage = (label, error) => {
    if (error) {
      return `${label} not sent: ${error}\n`;
    } else {
      M.toast({ html: `Invite code sent by ${label}` });
      return '';
    }
  };

  const clearFields = () => {
    setInviteCode('');
    setErrorMessageInModal(null);
    setCodeEdited(false);
    setCurrentProfile(null);
    setDeleteResults(null);
    setEmailConfirm('');
  };

  const showDeleteConfirmation = (profile) => {
    setCurrentProfile(profile);
    M.Modal.getInstance(refModalDelete.current).open();
  };

  const deleteFullProfile = () => {
    if (!currentProfile) return;
    console.log(currentProfile);
    setIsLoading(true);
    Functions.deleteFullProfile(currentProfile.id)
      .then(({ data }) => {
        M.toast({ html: 'User deleted' });
        setDeleteResults(data);
        updateProfiles();
      })
      .catch((e) => M.toast({ html: e.message ? e.message : 'Something went wrong' }))
      .finally(() => setIsLoading(false));
  };

  const renderEmailDeleteConfirmation = () => (
    <div>
      <h4>Delete this user?</h4>
      <p>This action will permanently delete all the data for this user.</p>
      {currentProfile && currentProfile.email && (
        <div>
          <p>
            Confirm you want to delete this user by typing its email: <b>{currentProfile.email}</b>
          </p>
          <input type="text" placeholder={currentProfile.email} value={emailConfirm} onChange={(e) => setEmailConfirm(e.target.value)} />
        </div>
      )}
    </div>
  );

  const renderDeleteResults = () => (
    <div>
      <h4>Results</h4>
      <div>{<JSONPretty id="json-pretty" data={deleteResults}></JSONPretty>}</div>
    </div>
  );

  const renderDeleteDialogButtons = () =>
    deleteResults ? (
      <button className="modal-close waves-effect waves-light btn-small" onClick={() => clearFields()}>
        OK
      </button>
    ) : (
      <Fragment>
        <button disabled={isLoading || (currentProfile && emailConfirm !== currentProfile.email)} className="waves-effect waves-light btn-small" onClick={() => deleteFullProfile()}>
          Confirm
        </button>
        <button disabled={isLoading} className="modal-close btn-flat btn-small" onClick={() => clearFields()}>
          Cancel
        </button>
      </Fragment>
    );

  const renderConfirmDeleteDialog = () => {
    return (
      <div ref={refModalDelete} id="modalDelete" className="modal">
        <div className="modal-content">
          {deleteResults ? renderDeleteResults() : renderEmailDeleteConfirmation()}
          {isLoading && renderLoading()}
        </div>
        <div className="modal-footer">{renderDeleteDialogButtons()}</div>
      </div>
    );
  };

  const renderInviteCodeDialog = () => {
    return (
      <div ref={refModal} id="modal1" className="modal">
        <div className="modal-content">
          <h5>Invite code</h5>
          {isLoading ? renderLoading() : <input type="text" value={inviteCode} onChange={(e) => editCode(e.target.value)} />}
          {renderErrorMessage()}
        </div>
        {renderFooter()}
      </div>
    );
  };

  const renderLoading = () => (
    <div className="progress" style={{ visibility: isLoading ? 'visible' : 'hidden' }}>
      <div className="indeterminate"></div>
    </div>
  );

  const renderErrorMessage = () => errorMessageInModal && <p style={{ color: 'red', whiteSpace: 'pre-line' }}>{errorMessageInModal}</p>;

  const renderFooter = () => (
    <div className="modal-footer">
      <Link to={window.location} onClick={() => sendEmail()} disabled={!inviteCode || isLoading} className="waves-effect btn btn-flat">
        Send invite code
      </Link>
      <Link to={window.location} onClick={() => clearFields()} className="modal-close waves-effect btn btn-flat">
        Cancel
      </Link>
    </div>
  );

  return (
    <div className="company-list section">
      {renderInviteCodeDialog()}
      {renderConfirmDeleteDialog()}
      {profiles &&
        profiles.map((profile) => {
          if (profile.email && profile.name) {
            return <ProfileSummary key={profile.id || profile.email} profile={profile} showInviteCodeModal={openModal} sent={sent} showDeleteConfirmation={showDeleteConfirmation} updateProfiles={updateProfiles} />;
          } else {
            return '';
          }
        })}
    </div>
  );
};

export default ProfileList;
